// src/pages/Admin/AdminPage.js
// Back-office : dashboard, produits, commandes, avis, clients, messages.
import { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../services/api';
import { useCategories } from '../../hooks/useProducts';
import './AdminPage.css';

import { useDocumentTitle } from '../../hooks/useDocumentTitle';
const STATUT_LABEL = {
  en_attente: 'En attente', confirme: 'Confirmé', expedie: 'Expédié',
  livre: 'Livré', annule: 'Annulé',
};

const TABS = [
  ['dashboard', 'Tableau de bord'],
  ['produits',  'Produits'],
  ['commandes', 'Commandes'],
  ['avis',      'Avis'],
  ['clients',   'Clients'],
  ['messages',  'Messages'],
];

export default function AdminPage() {
  useDocumentTitle('Administration');

  const [tab, setTab] = useState('dashboard');

  return (
    <div className="admin">
      <div className="container admin__inner">
        <p className="admin__overtitle">Back-office</p>
        <h1 className="admin__titre">Administration</h1>

        <nav className="admin__tabs" aria-label="Sections administration">
          {TABS.map(([key, label]) => (
            <button key={key}
              className={`admin__tab${tab === key ? ' active' : ''}`}
              onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </nav>

        {tab === 'dashboard' && <DashboardTab />}
        {tab === 'produits'  && <ProduitsTab />}
        {tab === 'commandes' && <CommandesTab />}
        {tab === 'avis'      && <AvisTab />}
        {tab === 'clients'   && <ClientsTab />}
        {tab === 'messages'  && <MessagesTab />}
      </div>
    </div>
  );
}

/* ── TABLEAU DE BORD ─────────────────────────────────── */
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminService.dashboard().then(setStats).catch(e => setError(e.message));
  }, []);

  if (error)  return <p className="admin__error">{error}</p>;
  if (!stats) return <div className="spinner" />;

  return (
    <div>
      <div className="admin__stats">
        {[
          ['Chiffre d’affaires', `${stats.ca_total.toFixed(2)} €`],
          ['Commandes', stats.nb_commandes],
          ['Clients', stats.nb_clients],
          ['Produits actifs', stats.nb_produits],
        ].map(([label, val]) => (
          <div key={label} className="admin__stat-card">
            <p className="admin__stat-val">{val}</p>
            <p className="admin__stat-label">{label}</p>
          </div>
        ))}
      </div>

      <div className="admin__cols">
        <section className="admin__card">
          <h2>Dernières commandes</h2>
          {stats.dernieres_commandes.length === 0 ? <p className="admin__vide">Aucune commande.</p> : (
            stats.dernieres_commandes.map(o => (
              <div key={o.id} className="admin__row">
                <span>#{o.id}</span>
                <span>{new Date(o.date_commande).toLocaleDateString('fr-FR')}</span>
                <span className={`admin__statut admin__statut--${o.statut}`}>{STATUT_LABEL[o.statut] || o.statut}</span>
                <span className="admin__prix">{o.total.toFixed(2)} €</span>
              </div>
            ))
          )}
        </section>
        <section className="admin__card">
          <h2>Alertes stock (≤ 5)</h2>
          {stats.alertes_stock.length === 0 ? <p className="admin__vide">Aucune alerte.</p> : (
            stats.alertes_stock.map(p => (
              <div key={p.id} className="admin__row">
                <span>{p.nom}</span>
                <span className={p.stock === 0 ? 'admin__stock-zero' : 'admin__stock-bas'}>
                  {p.stock === 0 ? 'Épuisé' : `${p.stock} restant(s)`}
                </span>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}

/* ── PRODUITS ─────────────────────────────────────────── */
const PRODUIT_VIDE = { nom: '', description: '', prix: '', prix_barre: '', stock: 0, categorie_id: '', image: '', nouveaute: false };

function ProduitsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(null);   // null | {…PRODUIT_VIDE} | produit à éditer
  const [error, setError]       = useState('');
  const categories = useCategories();

  const load = useCallback(() => {
    setLoading(true);
    adminService.getProducts().then(setProducts).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  function openEdit(p) {
    setForm({ ...p, prix_barre: p.prix_barre ?? '', image: p.images?.[0] || '' });
    setError('');
  }

  async function save(e) {
    e.preventDefault();
    setError('');
    const payload = {
      nom: form.nom, description: form.description,
      prix: parseFloat(form.prix),
      prix_barre: form.prix_barre === '' ? null : parseFloat(form.prix_barre),
      stock: parseInt(form.stock, 10) || 0,
      categorie_id: parseInt(form.categorie_id, 10),
      images: form.image ? [form.image] : [],
      nouveaute: !!form.nouveaute,
    };
    try {
      if (form.id) await adminService.updateProduct(form.id, payload);
      else         await adminService.createProduct(payload);
      setForm(null);
      load();
    } catch (err) { setError(err.message); }
  }

  async function toggleActif(p) {
    try { await adminService.updateProduct(p.id, { actif: !p.actif }); load(); }
    catch (err) { setError(err.message); }
  }

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="admin__toolbar">
        <button className="btn-or" onClick={() => { setForm({ ...PRODUIT_VIDE }); setError(''); }}>+ Nouveau produit</button>
      </div>
      {error && <p className="admin__error">{error}</p>}

      {form && (
        <form className="admin__card admin__form" onSubmit={save}>
          <h2>{form.id ? `Modifier — ${form.nom}` : 'Nouveau produit'}</h2>
          <div className="admin__form-grid">
            <label>Nom *
              <input required value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} />
            </label>
            <label>Catégorie *
              <select required value={form.categorie_id} onChange={e => setForm(f => ({ ...f, categorie_id: e.target.value }))}>
                <option value="">— Choisir —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </label>
            <label>Prix (€) *
              <input required type="number" step="0.01" min="0" value={form.prix} onChange={e => setForm(f => ({ ...f, prix: e.target.value }))} />
            </label>
            <label>Prix barré (€)
              <input type="number" step="0.01" min="0" value={form.prix_barre} onChange={e => setForm(f => ({ ...f, prix_barre: e.target.value }))} />
            </label>
            <label>Stock
              <input type="number" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
            </label>
            <label>Image (chemin)
              <input placeholder="/images/produits/mon-produit.jpg" value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))} />
            </label>
          </div>
          <label>Description
            <textarea rows="10" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </label>
          <label className="admin__check">
            <input type="checkbox" checked={!!form.nouveaute} onChange={e => setForm(f => ({ ...f, nouveaute: e.target.checked }))} />
            Nouveauté
          </label>
          <div className="admin__form-actions">
            <button type="submit" className="btn-or">{form.id ? 'Enregistrer' : 'Créer'}</button>
            <button type="button" className="btn-outline" onClick={() => setForm(null)}>Annuler</button>
          </div>
        </form>
      )}

      <div className="admin__table-wrap">
        <table className="admin__table">
          <thead>
            <tr><th>Image</th><th>Nom</th><th>Prix</th><th>Stock</th><th>Statut</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.id} className={p.actif ? '' : 'inactif'}>
                <td>
                  {p.images?.[0]
                    ? <img src={p.images[0]} alt="" className="admin__thumb" />
                    : <span className="admin__thumb admin__thumb--vide">—</span>}
                </td>
                <td>{p.nom}{p.nouveaute && <span className="admin__badge">Nouveau</span>}</td>
                <td>{p.prix.toFixed(2)} €</td>
                <td className={p.stock <= 5 ? 'admin__stock-bas' : ''}>{p.stock}</td>
                <td>{p.actif ? 'Actif' : 'Désactivé'}</td>
                <td className="admin__actions">
                  <button onClick={() => openEdit(p)}>Modifier</button>
                  <button onClick={() => toggleActif(p)}>{p.actif ? 'Désactiver' : 'Réactiver'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── COMMANDES ────────────────────────────────────────── */
function CommandesTab() {
  const [data, setData]       = useState({ orders: [], total: 0, pages: 1 });
  const [page, setPage]       = useState(1);
  const [statut, setStatut]   = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminService.getOrders({ page, ...(statut ? { statut } : {}) })
      .then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [page, statut]);
  useEffect(() => { load(); }, [load]);

  async function changerStatut(o, nouveau) {
    try { await adminService.updateOrder(o.id, { statut: nouveau }); load(); }
    catch (err) { setError(err.message); }
  }

  async function changerSuivi(o, numero) {
    try { await adminService.updateOrder(o.id, { numero_suivi: numero }); }
    catch (err) { setError(err.message); }
  }

  return (
    <div>
      <div className="admin__toolbar">
        <select value={statut} onChange={e => { setStatut(e.target.value); setPage(1); }}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="admin__count">{data.total} commande(s)</span>
      </div>
      {error && <p className="admin__error">{error}</p>}
      {loading ? <div className="spinner" /> : data.orders.length === 0 ? <p className="admin__vide">Aucune commande.</p> : (
        <div className="admin__orders">
          {data.orders.map(o => (
            <div key={o.id} className="admin__card admin__order">
              <div className="admin__order-head">
                <strong>#{o.id}</strong>
                <span>{new Date(o.date_commande).toLocaleString('fr-FR')}</span>
                <span className="admin__prix">{o.total.toFixed(2)} €</span>
                <select value={o.statut} onChange={e => changerStatut(o, e.target.value)}>
                  {Object.entries(STATUT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <p className="admin__order-items">
                {o.items.map(i => `${i.nom} ×${i.quantite}`).join(' · ')}
              </p>
              {o.adresse && (
                <p className="admin__order-adresse">
                  📦 {[o.adresse.rue, o.adresse.code_postal, o.adresse.ville].filter(Boolean).join(', ')}
                </p>
              )}
              <div className="admin__order-suivi">
                <input defaultValue={o.numero_suivi || ''} placeholder="N° de suivi"
                  onBlur={e => e.target.value !== (o.numero_suivi || '') && changerSuivi(o, e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      )}
      {data.pages > 1 && (
        <div className="admin__pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Précédent</button>
          <span>Page {page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Suivant →</button>
        </div>
      )}
    </div>
  );
}

/* ── AVIS (modération) ────────────────────────────────── */
function AvisTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminService.getReviews().then(setReviews).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function approuver(id) {
    try { await adminService.approveReview(id); load(); } catch (err) { setError(err.message); }
  }
  async function supprimer(id) {
    try { await adminService.deleteReview(id); load(); } catch (err) { setError(err.message); }
  }

  if (loading) return <div className="spinner" />;
  return (
    <div>
      {error && <p className="admin__error">{error}</p>}
      {reviews.length === 0 ? <p className="admin__vide">Aucun avis en attente de modération.</p> : (
        reviews.map(r => (
          <div key={r.id} className="admin__card admin__review">
            <div className="admin__review-head">
              <span className="admin__stars">{'★'.repeat(r.note)}{'☆'.repeat(5 - r.note)}</span>
              <span className="admin__count">Produit #{r.product_id} — Client #{r.user_id}</span>
            </div>
            <p>{r.commentaire || <em>(sans commentaire)</em>}</p>
            <div className="admin__form-actions">
              <button className="btn-or" onClick={() => approuver(r.id)}>Approuver</button>
              <button className="btn-outline" onClick={() => supprimer(r.id)}>Supprimer</button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ── CLIENTS ──────────────────────────────────────────── */
function ClientsTab() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    adminService.getUsers().then(setUsers).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner" />;
  return (
    <div className="admin__table-wrap">
      {error && <p className="admin__error">{error}</p>}
      <table className="admin__table">
        <thead><tr><th>Nom</th><th>Prénom</th><th>Email</th><th>Inscrit le</th></tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td>{u.nom}</td><td>{u.prenom}</td><td>{u.email}</td>
              <td>{new Date(u.date_inscription).toLocaleDateString('fr-FR')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {users.length === 0 && <p className="admin__vide">Aucun client inscrit.</p>}
    </div>
  );
}

/* ── MESSAGES DE CONTACT ──────────────────────────────── */
function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminService.getMessages().then(setMessages).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function marquerLu(id) {
    try { await adminService.readMessage(id); load(); } catch (err) { setError(err.message); }
  }

  if (loading) return <div className="spinner" />;
  return (
    <div>
      {error && <p className="admin__error">{error}</p>}
      {messages.length === 0 ? <p className="admin__vide">Aucun message reçu.</p> : (
        messages.map(m => (
          <div key={m.id} className={`admin__card admin__message${m.lu ? ' lu' : ''}`}>
            <div className="admin__message-head">
              <strong>{m.objet}</strong>
              {!m.lu && <span className="admin__badge">Non lu</span>}
              <span className="admin__count">{new Date(m.date).toLocaleString('fr-FR')}</span>
            </div>
            <p className="admin__message-de">De : {m.nom} — <a href={`mailto:${m.email}`}>{m.email}</a></p>
            <p>{m.message}</p>
            {!m.lu && (
              <div className="admin__form-actions">
                <button className="btn-outline" onClick={() => marquerLu(m.id)}>Marquer comme lu</button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
